import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowLeft, ArrowRight, AlertTriangle, Sparkles, Camera, Loader2, UserCheck, Wand2 } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import JobPhotoGrid, { MAX_JOB_PHOTOS } from "@/components/uploads/JobPhotoGrid";
import { INVITE_CLIENT_FIRST_JOB, armPushInvite } from "@/lib/push";
import { armFeedbackPrompt } from "@/lib/feedbackPrompt";
import api, { mediaUrl } from "@/lib/api";
import { useUploadLimitMb } from "@/lib/limits";
import { aiErrorText, errorCode, gateErrorText } from "@/lib/errors";
import { categoryLabel, subcategoryLabel } from "@/lib/catalog";
import { useAuth } from "@/contexts/AuthContext";
import { jobDetailPath, ownJobsPath } from "@/lib/ownerRoutes";
import DateField from "@/components/forms/DateField";

// The text the classifier was last asked about, normalized the way the server
// normalizes its cache key. Module scope on purpose: inside the component it
// would be a new function on every render, and therefore a dependency of the
// callback below that changes every render.
const inferKey = (title, description) =>
  `${title || ""} ${description || ""}`.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * The classic job form. Mounted at `/client/jobs/new` and, since DY2.6, at
 * `/provider/requests/new` — the same page, because posting a job never
 * depended on which side of the marketplace the poster is on. Only the
 * navigation around it is namespaced.
 *
 * `?for=<public_slug>` makes it a *directed* request ([FIX] 2026-09-07): the
 * job is posted for that one FixyBee, only they are notified, and FixyMatch
 * never runs. The slug arrives from the "Invitar a cotizar" button on their
 * public page, which until this fix carried nothing and let the request go out
 * to the whole category.
 *
 * In that mode the trade is not asked for ([FIX] 2026-09-09): the FixyBee
 * already answers it. One category on their profile and the selectors are
 * replaced by a line of text; several and the selector lists theirs and never
 * the catalog. `create_job` refuses a category they do not serve either way —
 * this is the form being honest, not the rule.
 *
 * **In the ordinary flow the trade is not asked for either ([FEAT]
 * 2026-09-09).** The requester describes, the system classifies, the FixyBee
 * declares: `POST /ai/classify-job` reads what they wrote and the answer comes
 * back as a chip they can change, not as a selector they have to fill. The
 * specialty is gone from this form entirely — it is the FixyBee's word about
 * themselves, and FixyMatch dispatches on the category alone, so a job without
 * one still reaches every FixyBee of its trade.
 *
 * The inference is an improvement on the form and never a gate in front of it.
 * Short text, a low-confidence answer, an AI outage, a quota that ran out —
 * every one of them puts the classic selector back on screen with no error and
 * no blocked publish. And once the requester touches that selector, inference
 * stops for good: their choice is not something to be second-guessed by the
 * next blur.
 */
export default function PostJob() {
  const { t } = useTranslation();
  const uploadLimitMb = useUploadLimitMb();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // T19 F3. Mounted at `/{client|provider}/jobs/:jobId/edit`, the same form
  // edits one of the owner's open jobs: precharged from the job, saved with
  // `PATCH /jobs/{id}`. No trade inference — the trade is already set — and
  // the trade is locked once anybody has quoted (X3), which the server
  // enforces and this form only says out loud.
  const { jobId: editingId } = useParams();
  const editing = !!editingId;
  const [categoryLocked, setCategoryLocked] = useState(false);
  const originalCategory = useRef("");
  const { user } = useAuth();
  const role = user?.profile?.role;
  const forSlug = searchParams.get("for") || "";
  // null = not asked or still loading; false = asked and unresolvable.
  const [directedTo, setDirectedTo] = useState(null);
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category_id: "",
    subcategory_id: "",
    budget_min: 50,
    budget_max: 200,
    city: user?.profile?.location?.city || "",
    state: user?.profile?.location?.state || "",
    preferred_start_date: "",
    preferred_end_date: "",
    photo_urls: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [aiLoading, setAiLoading] = useState("");
  const [dateMode, setDateMode] = useState("asap");
  // T21-M5. Whether each date field holds something it can send; text it
  // cannot read emits "", and "" must not go out as "no date preferred".
  const [dateValid, setDateValid] = useState({ start: true, end: true });
  const [showDateErrors, setShowDateErrors] = useState(false);
  const datesInvalid = dateMode === "specific" && !(dateValid.start && dateValid.end);
  const [aiEstimate, setAiEstimate] = useState(null);
  // --- the inferred trade ------------------------------------------------
  // `inferred` is the catalog row the classifier returned, shown as a chip.
  // `pickerOpen` is the requester having asked for the selector, which turns
  // inference off for good. `inferFailed` is every way the answer can fail to
  // arrive — too short, low confidence, no match, an error — all of which show
  // the selector and none of which show an error.
  const [inferred, setInferred] = useState(null);
  const [inferring, setInferring] = useState(false);
  const [inferFailed, setInferFailed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // The normalized text already sent, so a blur that changed nothing costs
  // nothing. The server's 24h cache would answer free anyway; the round trip
  // and the spinner would not.
  const askedFor = useRef("");
  // The call in flight and the text it was made for, so "Siguiente" can wait
  // for the one the blur just started instead of firing a second. Keyed, not
  // bare: clicking the button blurs the textarea first, and the click handler
  // still holds the render from before that blur — a bare flag would read
  // "nothing in flight" at the exact moment there is.
  const inFlight = useRef(null);
  const [advancing, setAdvancing] = useState(false);

  // Edit mode: load the job into the form. The selector is opened from the
  // start (`pickerOpen`), which is also what keeps inference off for good.
  useEffect(() => {
    if (!editing) return;
    let live = true;
    (async () => {
      try {
        const { data: job } = await api.get(`/jobs/${editingId}`);
        if (!live) return;
        originalCategory.current = job.category_id || "";
        setForm((f) => ({
          ...f,
          title: job.title || "",
          description: job.description || "",
          category_id: job.category_id || "",
          subcategory_id: job.subcategory_id || "",
          budget_min: job.budget_min ?? 0,
          budget_max: job.budget_max ?? 0,
          city: job.location?.city || "",
          state: job.location?.state || "",
          preferred_start_date: job.preferred_start_date || "",
          preferred_end_date: job.preferred_end_date || "",
          photo_urls: (job.photos || []).map((ph) => ph.url),
        }));
        setDateMode(job.preferred_start_date || job.preferred_end_date ? "specific" : "asap");
        setPickerOpen(true);
        const { data: q } = await api.get(`/quotes?job_id=${editingId}`);
        if (!live) return;
        setCategoryLocked((q.quotes || []).some((x) => x.status !== "withdrawn"));
      } catch (e) {
        if (live) setError(gateErrorText(e, t, t("postJobX.failed")));
      }
    })();
    return () => { live = false; };
  }, [editing, editingId]); // eslint-disable-line

  // An ordinary post is one with no FixyBee behind it — including a `?for=`
  // slug that no longer resolves, which `handleSubmit` posts to everyone.
  // While the slug is still resolving it is neither: nothing is inferred and
  // the form waits, because a chip that appears and is then replaced by a
  // FixyBee's fixed trade is worse than a moment of the plain selector.
  const ordinary = !forSlug || directedTo === false;

  const improveDescription = async () => {
    if (form.description.length < 5) return;
    setAiLoading("desc"); setError("");
    try {
      const { data } = await api.post("/ai/smart-description", { title: form.title, current_text: form.description });
      // Two fields, each into its own input. The API returns them separated
      // now; it used to return one blob that carried the prompt's own labels
      // ("Title: ... / Current Description: ...") straight into the textarea.
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
      }));
    } catch (e) {
      setError(aiErrorText(e, t, t("postJobX.aiBusy")));
    } finally { setAiLoading(""); }
  };

  const diagnoseFromImage = async () => {
    const firstUrl = form.photo_urls.find((u) => !!u);
    if (!firstUrl) { setError(t("postJobX.uploadFirst")); return; }
    const absUrl = mediaUrl(firstUrl);
    setAiLoading("img"); setError("");
    try {
      const { data } = await api.post("/ai/image-diagnosis", { image_url: absUrl, user_hint: form.title });
      if (data.parsed) {
        setForm((f) => ({
          ...f,
          title: data.title || f.title,
          description: data.description || f.description,
          category_id: data.category_id || f.category_id,
          subcategory_id: data.category_id ? "" : f.subcategory_id,
        }));
        // The photo is a second way to answer the same question, so the chip
        // has to follow it. Without this, stepping back to step 1 would show
        // the trade the words suggested while the form carried the one the
        // picture did.
        if (data.category_id && ordinary && !pickerOpen) {
          const row = categories.find((c) => c.category_id === data.category_id);
          if (row) { setInferred(row); setInferFailed(false); }
        }
      } else { setError(t("postJobX.parseFail")); }
    } catch (e) {
      setError(aiErrorText(e, t, t("postJobX.aiVisionBusy")));
    } finally { setAiLoading(""); }
  };

  const getEstimate = async () => {
    if (!form.category_id || form.description.length < 10) return;
    setAiLoading("est"); setError("");
    try {
      const { data } = await api.post("/ai/estimator", {
        category_id: form.category_id, subcategory_id: form.subcategory_id || null,
        description: form.description, state: form.state || "",
      });
      setAiEstimate(data);
      if (data.min_usd && data.max_usd) {
        setForm((f) => ({ ...f, budget_min: data.min_usd, budget_max: data.max_usd }));
      }
    } catch (e) {
      setError(aiErrorText(e, t, t("postJobX.aiEstBusy")));
    } finally { setAiLoading(""); }
  };

  /**
   * Ask the server which trade this is. Resolves to the catalog row it chose,
   * or null — and null is a normal outcome, not an error: the caller shows the
   * selector. Never throws.
   */
  const inferCategory = useCallback(async () => {
    if (!ordinary || pickerOpen) return null;
    const key = inferKey(form.title, form.description);
    // The server refuses to read anything shorter, so do not pay for the trip.
    if (key.length < 12 || key === askedFor.current) return null;
    askedFor.current = key;
    setInferring(true);
    try {
      const { data } = await api.post("/ai/classify-job", {
        title: form.title, description: form.description,
      });
      const top = (data.categories || [])[0];
      // "low" is the model saying it guessed. A guess shown as a chip reads as
      // a fact, so it is not shown at all.
      if (top && data.confidence !== "low") {
        // The catalog row when it is loaded, the API's `{category_id, name}`
        // otherwise: `categoryLabel` needs the slug to find the translation,
        // and the chip must read "Plomería" to a Spanish requester rather than
        // the English name the classifier answered with.
        setInferred(categories.find((c) => c.category_id === top.category_id) || top);
        setInferFailed(false);
        setForm((f) => ({ ...f, category_id: top.category_id, subcategory_id: "" }));
        return top;
      }
      setInferred(null);
      setInferFailed(true);
      return null;
    } catch (e) {
      // Budget spent, quota exhausted, model down, network gone: the requester
      // is not told about any of it. They get the form they had before.
      console.warn("[PostJob] category inference unavailable", e);
      setInferred(null);
      setInferFailed(true);
      return null;
    } finally {
      setInferring(false);
    }
  }, [ordinary, pickerOpen, categories, form.title, form.description]);

  // The trigger: leaving the description. It is the last thing they write
  // before the trade would have been asked for, so the answer is on screen by
  // the time they look for it — and it is one call per finished description
  // rather than one per keystroke or one per field.
  const inferOnBlur = () => {
    const key = inferKey(form.title, form.description);
    inFlight.current = { key, promise: inferCategory() };
  };

  // Editing the text again reopens the possibility, but only while nothing has
  // been chosen: a selector must never vanish out from under a requester who is
  // using it.
  const textChanged = () => {
    if (inferFailed && !pickerOpen && !form.category_id) setInferFailed(false);
  };

  const openPicker = () => {
    setPickerOpen(true);
    setInferred(null);
  };

  /**
   * Leaving step 1. The category is still required to go on — `create_job`
   * needs one and the estimator on step 2 reads it — but it is no longer the
   * requester's job to produce it. If none is set yet, this waits for the call
   * the blur started (or starts one), and only falls back to the selector when
   * that comes back with nothing.
   */
  const goToStep2 = async () => {
    if (form.category_id) { setStep(2); return; }
    // Same condition as `showPicker` below, spelled out: with the selector on
    // screen the button is disabled anyway, and reading a `const` declared
    // further down is a lint error waiting for a CI that treats it as one.
    if (!ordinary || pickerOpen || inferFailed) return;
    setAdvancing(true);
    try {
      const key = inferKey(form.title, form.description);
      if (!inFlight.current || inFlight.current.key !== key) {
        inFlight.current = { key, promise: inferCategory() };
      }
      const top = await inFlight.current.promise;
      if (top) setStep(2);
      else setInferFailed(true);
    } finally {
      setAdvancing(false);
    }
  };

  // Who this request is for, when it came from a FixyBee's page. Resolved here
  // rather than passed in the URL because the name has to be shown — a form
  // that silently behaves differently is the same class of bug as one that
  // silently drops the direction.
  useEffect(() => {
    if (!forSlug) { setDirectedTo(null); return; }
    let live = true;
    api.get(`/users/providers/${encodeURIComponent(forSlug)}`)
      .then((r) => { if (live) setDirectedTo(r.data); })
      // A slug that no longer resolves does not block the form — but it must
      // not silently become an ordinary post either, which is exactly the
      // failure being fixed. `false` renders the warning below.
      .catch(() => { if (live) setDirectedTo(false); });
    return () => { live = false; };
  }, [forSlug]);

  useEffect(() => {
    api.get("/catalog/categories")
      .then((r) => setCategories(r.data.categories || []))
      // A category list that silently fails to load is a form that cannot be
      // submitted, with nothing on screen or in the console saying why.
      .catch((e) => console.warn("[PostJob] categories failed to load", e));
  }, []);

  // Only directed mode has anywhere to put these now. Fetching them for an
  // ordinary post would be a request whose answer nothing on screen can show.
  useEffect(() => {
    if (form.category_id && directedTo) {
      api.get(`/catalog/subcategories?category_id=${form.category_id}`)
        .then((r) => setSubcategories(r.data.subcategories || []))
        .catch((e) => console.warn("[PostJob] subcategories failed to load", e));
    } else setSubcategories([]);
  }, [form.category_id, directedTo]);

  // What the FixyBee this request is aimed at actually does. Their public
  // payload carries both lists already, so the restriction costs no request.
  const directedCatIds = useMemo(
    () => (directedTo ? directedTo.service_categories || [] : []),
    [directedTo]
  );
  const directedSubIds = useMemo(
    () => (directedTo ? directedTo.service_subcategories || [] : []),
    [directedTo]
  );
  // A FixyBee with no declared trade is not restricted — there is nothing to
  // restrict to, and the server does not check them either.
  const restrictCats = directedCatIds.length > 0;
  const catOptions = restrictCats
    ? categories.filter((c) => directedCatIds.includes(c.category_id))
    : categories;
  // One trade: nothing to choose. The category is a fact about them, shown as
  // text and set below.
  const lockedCategory =
    restrictCats && catOptions.length === 1 ? catOptions[0] : null;
  // In directed mode the specialties on offer are theirs and only theirs, and
  // a FixyBee who lists none inside the chosen trade leaves the field with
  // nothing to say — so it does not appear at all. The open form is untouched:
  // there the full catalog shows, disabled when the category has no specialty.
  const subOptions = directedTo
    ? subcategories.filter((s) => directedSubIds.includes(s.subcategory_id))
    : subcategories;
  // **The specialty is a directed-mode field now** ([FEAT] 2026-09-09). In the
  // ordinary flow it is not asked, not shown and not sent: it is the FixyBee's
  // declaration about themselves, and nobody with a clogged sink knows whether
  // that is "leak detection" or "drain cleaning". FixyMatch filters candidates
  // by category and scores the specialty as a bonus, so the job still reaches
  // every FixyBee of its trade — the specialists among them included.
  const showSubcategory = !!directedTo && subOptions.length > 0;
  // The classic selector, as an escape hatch rather than the default: the
  // requester asked for it, or the inference could not answer.
  const showPicker = !ordinary || pickerOpen || inferFailed;

  // Keep the form inside those bounds whatever put a value in it — the locked
  // category, a category that stopped being allowed, an AI image diagnosis that
  // guessed a trade this FixyBee does not do.
  useEffect(() => {
    if (!restrictCats) return;
    setForm((f) => {
      if (lockedCategory) {
        return f.category_id === lockedCategory.category_id
          ? f
          : { ...f, category_id: lockedCategory.category_id, subcategory_id: "" };
      }
      if (f.category_id && !directedCatIds.includes(f.category_id)) {
        return { ...f, category_id: "", subcategory_id: "" };
      }
      return f;
    });
    // `form.category_id` is a dependency on purpose: the value can also be put
    // there by the AI image diagnosis, after everything else has settled. The
    // updater returns the same object when the value is already in bounds, so
    // an in-bounds render does not re-render.
  }, [restrictCats, lockedCategory, directedCatIds, form.category_id]);

  // Same for the specialty, and for every directed request rather than only
  // the restricted ones: what the field cannot show, the form does not send.
  useEffect(() => {
    if (!directedTo) return;
    setForm((f) =>
      f.subcategory_id && !directedSubIds.includes(f.subcategory_id)
        ? { ...f, subcategory_id: "" }
        : f
    );
  }, [directedTo, directedSubIds, form.subcategory_id]);

  /**
   * T19 F3. Save an edit. Only what the form actually holds goes out, and the
   * trade only when it changed — `PATCH` reads a category it was sent as a
   * request to move the job, which is the one thing it refuses once anybody
   * has quoted. An empty date is sent as "" and not null, because "" is how
   * the endpoint is told to CLEAR a date ("as soon as possible"), while a
   * missing field means "leave it as it was".
   */
  const saveEdit = async () => {
    setError("");
    setSubmitting(true);
    const body = {
      title: form.title,
      description: form.description,
      budget_min: Number(form.budget_min) || 0,
      budget_max: Number(form.budget_max) || 0,
      city: form.city || "",
      state: form.state || "",
      preferred_start_date: form.preferred_start_date || "",
      preferred_end_date: form.preferred_end_date || "",
      photo_urls: form.photo_urls,
    };
    if (form.category_id && form.category_id !== originalCategory.current) {
      body.category_id = form.category_id;
    }
    try {
      await api.patch(`/jobs/${editingId}`, body);
      navigate(jobDetailPath(role, editingId), { replace: true });
    } catch (e) {
      const code = errorCode(e);
      setError(
        code === "job_category_locked" ? t("postJobX.categoryLocked")
          : code === "job_content_blocked" ? t("postJobX.flagged")
            : gateErrorText(e, t, t("postJobX.failed"))
      );
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Step 2 already refuses to be left like this; this is the backstop.
    if (datesInvalid) { setShowDateErrors(true); setStep(2); return; }
    if (editing) { await saveEdit(); return; }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_start_date: form.preferred_start_date || null,
        preferred_end_date: form.preferred_end_date || null,
        // Only when the FixyBee actually resolved. The server checks the id
        // again — it exists, it is a FixyBee, it is not the caller — and
        // refuses with a code rather than quietly posting to everyone.
        directed_provider_id: directedTo ? directedTo.user_id : null,
      };
      const { data } = await api.post("/jobs", payload);
      if (data.moderation_status === "flagged") {
        setError(t("postJobX.flagged"));
        setSubmitting(false);
        return;
      }
      // Their job is live and quotes are what happens next — the one moment a
      // client has a concrete reason to want an alert. Once ever, per the key
      // armPushInvite keeps.
      armPushInvite(INVITE_CLIENT_FIRST_JOB);
      // T15. One of the three moments the survey is allowed to ask. Armed and
      // not shown: this page is about to be replaced, and the host at the root
      // is what survives the navigation. The gate on the server decides
      // whether it is actually owed — "their FIRST job" needs no count here,
      // because `feedback_prompt_done` can only be spent once.
      armFeedbackPrompt();
      navigate(ownJobsPath(role), { replace: true });
    } catch (e) {
      setError(gateErrorText(e, t, t("postJobX.failed")));
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout role={role}>
      <div data-testid="post-job-page">
        <button onClick={() => navigate(editing
                  ? jobDetailPath(role, editingId)
                  : role === "provider" ? "/provider/dashboard" : "/client/dashboard")}
                className="fx-btn fx-btn--ghost mb-4" data-testid="back-to-dash">
          <ArrowLeft size={16} /> {t("common.back")}
        </button>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
          {editing ? t("jobDetailX.editJob") : t("postJob.title")}
        </h1>
        <p className="mt-1 text-smoke">{t("postJobX.stepSub", { n: step })}</p>

        {directedTo && (
          <div className="mt-4 p-3 rounded-sm bg-success/10 border border-success/30 flex items-start gap-2 max-w-2xl" data-testid="directed-banner">
            <UserCheck size={16} className="text-success mt-0.5 shrink-0" />
            <p className="text-xs text-worker">
              <Trans
                i18nKey="postJobX.directedTo"
                values={{ name: directedTo.company_name || directedTo.full_name }}
                components={{ strong: <strong /> }}
              />
            </p>
          </div>
        )}
        {directedTo === false && (
          <div className="mt-4 p-3 rounded-sm bg-danger/10 border border-danger/30 flex items-start gap-2 max-w-2xl" data-testid="directed-unresolved">
            <AlertTriangle size={16} className="text-danger mt-0.5 shrink-0" />
            <p className="text-xs text-worker">{t("postJobX.directedUnresolved")}</p>
          </div>
        )}

        <div className="mt-4 p-3 rounded-sm bg-gradient-to-r from-hive/20 to-cream border border-hive/30 flex items-start gap-2 max-w-2xl" data-testid="ai-banner">
          <Sparkles size={16} className="text-hive-ink mt-0.5 shrink-0" />
          <p className="text-xs text-worker">
            <Trans i18nKey="postJobX.aiBanner" components={{ strong: <strong />, em: <span className="font-display text-hive-ink" /> }} />
          </p>
        </div>

        {error && (
          <div className="mt-6 p-4 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" data-testid="post-job-error">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {step === 1 && (
          <div className="mt-8 space-y-5 stagger max-w-2xl">
            <div>
              <label className="label-base">{t("postJobX.needLabel")}</label>
              <input
                className="input-base" required value={form.title}
                onChange={(e) => { setForm({ ...form, title: e.target.value }); textChanged(); }}
                placeholder={t("postJobX.needPh")}
                data-testid="job-title"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label-base mb-0">{t("postJob.step1.describeLabel")}</label>
                <button
                  type="button"
                  onClick={improveDescription}
                  disabled={aiLoading === "desc" || form.description.length < 5}
                  className="text-xs font-display text-hive-ink hover:text-worker inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ai-improve-desc"
                >
                  {aiLoading === "desc" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {t("postJob.step1.aiImprove")}
                </button>
              </div>
              <textarea
                className="input-base min-h-[140px] mt-1.5" required maxLength={2000}
                value={form.description}
                onChange={(e) => { setForm({ ...form, description: e.target.value }); textChanged(); }}
                onBlur={inferOnBlur}
                placeholder={t("postJobX.describePh")}
                data-testid="job-description"
              />
              <p className="text-xs font-mono text-smoke mt-1">{form.description.length}/2000</p>
            </div>
            {/* `grid-cols-[minmax(0,1fr)]` for the single column below 640
                (T13-M2): the inferred-category pill inside carries a
                `truncate`, and a bare implicit track floors at that label's
                full unwrapped width. `sm:grid-cols-2` is unchanged and needs
                nothing — Tailwind emits `repeat(2, minmax(0,1fr))`. */}
            <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 gap-4">
              <div>
                <label className="label-base">{t("postJob.step2.categoryLabel")}</label>
                {lockedCategory ? (
                  <div data-testid="job-category-fixed">
                    <p className="input-base bg-cream/60 text-worker">
                      {categoryLabel(t, lockedCategory)}
                    </p>
                    <p className="text-xs text-smoke mt-1">
                      {t("postJobX.categoryFromProvider")}
                    </p>
                  </div>
                ) : inferring ? (
                  <p className="input-base bg-cream/60 text-smoke inline-flex items-center gap-2" data-testid="job-category-inferring">
                    <Loader2 size={14} className="animate-spin" /> {t("postJobX.inferring")}
                  </p>
                ) : inferred && !showPicker ? (
                  <div data-testid="job-category-inferred">
                    <div className="input-base bg-hive/15 border-hive/40 text-worker flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <Wand2 size={14} className="text-hive-ink shrink-0" />
                        <span className="truncate">
                          {t("postJobX.inferredIs", { category: categoryLabel(t, inferred) })}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={openPicker}
                        className="text-xs font-display text-hive-ink hover:text-worker underline shrink-0"
                        data-testid="job-category-change"
                      >
                        {t("postJobX.inferredChange")}
                      </button>
                    </div>
                    <p className="text-xs text-smoke mt-1">{t("postJobX.inferredHint")}</p>
                  </div>
                ) : showPicker ? (
                  <div>
                    <select className="input-base disabled:opacity-60 disabled:cursor-not-allowed" value={form.category_id}
                      onChange={(e) => { setPickerOpen(true); setInferred(null); setForm({ ...form, category_id: e.target.value, subcategory_id: "" }); }}
                      disabled={editing && categoryLocked}
                      data-testid="job-category"
                    >
                      <option value="">{t("postJobX.choose")}</option>
                      {catOptions.map((c) => (
                        <option key={c.category_id} value={c.category_id}>{categoryLabel(t, c)}</option>
                      ))}
                    </select>
                    {ordinary && inferFailed && !pickerOpen && (
                      <p className="text-xs text-smoke mt-1" data-testid="job-category-fallback">
                        {t("postJobX.inferredUnsure")}
                      </p>
                    )}
                    {editing && categoryLocked && (
                      <p className="text-xs text-smoke mt-1" data-testid="job-category-locked">
                        {t("postJobX.categoryLocked")}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Nothing typed yet, or typed and not yet left. The trade is
                     not a question here, so the space stays quiet until there
                     is something to say in it. */
                  <p className="text-xs text-smoke mt-2" data-testid="job-category-waiting">
                    {t("postJobX.inferredWaiting")}
                  </p>
                )}
              </div>
              {showSubcategory && (
                <div>
                  <label className="label-base">{t("postJob.step2.subcategoryLabel")}</label>
                  <select className="input-base" value={form.subcategory_id}
                    onChange={(e) => setForm({ ...form, subcategory_id: e.target.value })}
                    disabled={!subOptions.length}
                    data-testid="job-subcategory"
                  >
                    <option value="">{t("postJobX.choose")}</option>
                    {subOptions.map((s) => (
                      <option key={s.subcategory_id} value={s.subcategory_id}>{subcategoryLabel(t, s)}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              {/* With the selector on screen the gate is what it always was:
                  a category. Without it, the button waits for the inference the
                  blur just started — the alternative is a disabled button the
                  requester has to click twice for a reason nothing explains. */}
              <button
                onClick={goToStep2}
                disabled={
                  !form.title || form.description.length < 10 || advancing
                  || (showPicker && !form.category_id)
                }
                className="fx-btn fx-btn--primary"
                data-testid="job-next-1"
              >
                {advancing
                  ? <Loader2 size={18} className="animate-spin" />
                  : <>{t("postJob.next")} <ArrowRight size={18} /></>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 space-y-5 stagger max-w-2xl">
            <div>
              <div className="flex items-center justify-between">
                <label className="label-base mb-0">{t("postJobX.budgetLabel")}</label>
                <button type="button" onClick={getEstimate} disabled={aiLoading === "est"} className="text-xs font-display text-hive-ink hover:text-worker inline-flex items-center gap-1" data-testid="ai-estimate">
                  {aiLoading === "est" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {t("postJob.step2.aiEstimate")}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-1.5">
                <input type="number" min="0" className="input-base" value={form.budget_min}
                  onChange={(e) => setForm({ ...form, budget_min: e.target.value === "" ? "" : Number(e.target.value) })}
                  placeholder={t("postJobX.min")} data-testid="job-budget-min"
                />
                <input type="number" min="0" className="input-base" value={form.budget_max}
                  onChange={(e) => setForm({ ...form, budget_max: e.target.value === "" ? "" : Number(e.target.value) })}
                  placeholder={t("postJobX.max")} data-testid="job-budget-max"
                />
              </div>
              {aiEstimate?.rationale && (
                <p className="text-xs text-smoke mt-2 italic" data-testid="estimate-rationale">{t("postJobX.estimateNote", { rationale: aiEstimate.rationale })}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-base">{t("postJob.step3.cityLabel")}</label>
                <input className="input-base" value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="job-city" />
              </div>
              <div>
                <label className="label-base">{t("postJob.step3.stateLabel")}</label>
                <input className="input-base" value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="FL" data-testid="job-state" />
              </div>
            </div>
            <div>
              <label className="label-base mb-2">{t("postJobX.whenLabel")}</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-worker cursor-pointer">
                  <input type="radio" name="dateMode" value="asap" checked={dateMode === "asap"}
                    onChange={() => { setDateMode("asap"); setForm((f) => ({ ...f, preferred_start_date: "", preferred_end_date: "" })); }}
                    className="accent-hive-deep" data-testid="date-asap" />
                  {t("postJobX.asap")}
                </label>
                <label className="flex items-center gap-2 text-sm text-worker cursor-pointer">
                  <input type="radio" name="dateMode" value="specific" checked={dateMode === "specific"}
                    onChange={() => setDateMode("specific")}
                    className="accent-hive-deep" data-testid="date-specific" />
                  {t("postJobX.specificDates")}
                </label>
              </div>
              {dateMode === "specific" && (
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="label-base" htmlFor="job-start-date">{t("postJobX.startOpt")}</label>
                    <DateField id="job-start-date" value={form.preferred_start_date}
                      onChange={(preferred_start_date) => setForm((f) => ({ ...f, preferred_start_date }))}
                      onValidityChange={(start) => setDateValid((v) => ({ ...v, start }))}
                      showError={showDateErrors} data-testid="date-start" />
                  </div>
                  <div>
                    <label className="label-base" htmlFor="job-end-date">{t("postJobX.endOpt")}</label>
                    <DateField id="job-end-date" value={form.preferred_end_date}
                      onChange={(preferred_end_date) => setForm((f) => ({ ...f, preferred_end_date }))}
                      onValidityChange={(end) => setDateValid((v) => ({ ...v, end }))}
                      showError={showDateErrors} data-testid="date-end" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(1)} className="fx-btn fx-btn--ghost">{t("postJob.back")}</button>
              <button
                onClick={() => {
                  if (datesInvalid) { setShowDateErrors(true); return; }
                  setStep(3);
                }}
                disabled={form.budget_min < 0 || form.budget_max < form.budget_min}
                className="fx-btn fx-btn--primary" data-testid="job-next-2"
              >
                {t("postJob.next")} <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-8 space-y-5 stagger max-w-2xl">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label-base mb-0">{t("postJobX.photosLabel")}</label>
                <button
                  type="button"
                  onClick={diagnoseFromImage}
                  disabled={aiLoading === "img" || !form.photo_urls.some((u) => !!u)}
                  className="text-xs font-display text-hive-ink hover:text-worker inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ai-diagnose"
                >
                  {aiLoading === "img" ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                  {t("postJob.step3.aiDiagnose")}
                </button>
              </div>
              {/* The cap is the grid's, not a number retyped in a string: the
                  copy said five while the backend and the grid have always
                  taken eight. */}
              <p className="text-xs text-smoke mb-3">{t("postJobX.photosHint", { max: MAX_JOB_PHOTOS, mb: uploadLimitMb })}</p>

              <JobPhotoGrid
                urls={form.photo_urls}
                onChange={(next) => setForm({ ...form, photo_urls: next })}
              />
            </div>
            <div className="card-base p-5 bg-cream">
              <h3 className="font-display font-bold text-worker">{t("postJobX.readyTitle")}</h3>
              <p className="text-sm text-smoke mt-1">{t("postJobX.readyBody")}</p>
            </div>
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(2)} className="fx-btn fx-btn--ghost">{t("postJob.back")}</button>
              <button onClick={handleSubmit} disabled={submitting} className="fx-btn fx-btn--primary" data-testid="job-submit">
                {editing
                  ? (submitting ? t("jobDetailX.savingDots") : t("common.save"))
                  : (submitting ? t("postJobX.publishing") : t("postJobX.publishBtn"))}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
